import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { auth, functions } from '../firebase/config';
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  sendPasswordResetEmail,
  User,
  UserCredential,
  IdTokenResult,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import PageLoader from '../authentication/PageLoader';
import '../authentication/auth.css';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

interface UserCredentials {
  email: string;
  password: string;
}

interface Affiliation {
  schoolID: string | null;
  role: string;
}

interface FetchError extends Error {
  code?: string;
}

const isAffiliationArray = (value: unknown): value is Affiliation[] => {
  return Array.isArray(value) && value.every((item) => typeof item === 'object' && item !== null && 'role' in item && typeof item.role === 'string' && 'schoolID' in item && (typeof item.schoolID === 'string' || item.schoolID === null));
};

const PORTAL = 'owner';
const PORTAL_DISPLAY_NAME = 'Owner';
const OWNER_EMAIL = 'vochako7@gmail.com';

// Clear stale authentication data before login
const clearStaleAuthData = (currentUid?: string): void => {
  try {
    const cachedAuth = localStorage.getItem('cachedAuth');
    if (cachedAuth && currentUid) {
      try {
        const parsed = JSON.parse(cachedAuth);
        if (parsed.uid && parsed.uid !== currentUid) {
          console.log('Clearing cached auth for different user:', parsed.uid);
          localStorage.removeItem('cachedAuth');
        }
      } catch (e) {
        localStorage.removeItem('cachedAuth');
      }
    } else if (cachedAuth && !currentUid) {
      localStorage.removeItem('cachedAuth');
    }
    console.log('Stale auth data cleared if needed');
  } catch (error) {
    console.error('Error clearing stale auth data:', error);
  }
};

const OwnerLogin: React.FC = () => {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isLoaded, setIsLoaded] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [userCredentials, setUserCredentials] = useState<UserCredentials>({ email: '', password: '' });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState<string>('');
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  // Domain validation for cbsms-da9ce.web.app and localhost
  useEffect(() => {
    // Allowlist owner-login hostnames. Add Netlify hostnames (e.g. sonomaautomation.netlify.app)
    const hostname = window.location.hostname || '';
    const allowed = hostname.includes('cbsms-da9ce.web.app') || hostname === 'localhost' || hostname.includes('netlify.app') || hostname.includes('sonoma.school');
    if (!allowed) {
      setError('Owner login is only accessible from authorized domains');
      navigate('/landing-page');
    }
  }, [navigate]);

  const handleCredentials = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setUserCredentials((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validateInputs = (): void => {
    if (!userCredentials.email || !userCredentials.password) {
      throw new Error('Email and password must not be empty');
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userCredentials.email)) {
      throw new Error('Invalid email format');
    }
    if (userCredentials.password.length < 8) {
      throw new Error('Password must be at least 8 characters long');
    }
  };

  const restrictNonOwnerRoles = (affiliations: Affiliation[], email: string): void => {
    if (email !== OWNER_EMAIL) {
      // Non-owner emails must have NO affiliations to proceed
      if (affiliations.length > 0) {
        throw new Error('Access denied: Only owner accounts can sign in here.');
      }
    } else {
      // Owner email must have at least one owner affiliation
      // Note: Having additional roles (like guardian) is allowed for the owner
      const hasOwnerRole = affiliations.some(aff => aff.role === PORTAL);
      if (!hasOwnerRole) {
        throw new Error('Access denied: Owner role required.');
      }
    }
  };

  const setOwnerClaims = async (uid: string, email: string): Promise<void> => {
    if (email !== OWNER_EMAIL) {
      console.log('Not eligible for owner role:', email);
      return;
    }
    try {
      const setOwnerClaimsFn = httpsCallable(functions, 'setOwnerClaims');
      await setOwnerClaimsFn({ uid, email });
      console.log('Owner claims set for:', email);
    } catch (error: any) {
      console.error('Error setting owner claims:', error);
      throw new Error('Failed to set owner role. Please contact support.');
    }
  };

  const handleSignIn = async (): Promise<void> => {
    try {
      validateInputs();
      
  const userCredential: UserCredential = await signInWithEmailAndPassword(auth, userCredentials.email, userCredentials.password);
  const user: User = userCredential.user;
  // Clear stale cached data for different user (AFTER sign-in)
  clearStaleAuthData(user.uid);
      const idTokenResult = await user.getIdTokenResult(true);
      const affiliations = isAffiliationArray(idTokenResult.claims.affiliations)
        ? idTokenResult.claims.affiliations
        : [];
      const defaultAff = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;

      restrictNonOwnerRoles(affiliations, user.email || '');

      const ownerAffiliation = affiliations.find((aff) => aff.role === PORTAL);
      const schoolID = ownerAffiliation?.schoolID ?? 'cbsms-owner';

      if (!ownerAffiliation) {
        await setOwnerClaims(user.uid, user.email || '');
        const refreshedTokenResult = await user.getIdTokenResult(true);
        const updatedAffiliations = isAffiliationArray(refreshedTokenResult.claims.affiliations)
          ? refreshedTokenResult.claims.affiliations
          : [];
        const updatedOwnerAffiliation = updatedAffiliations.find((aff) => aff.role === PORTAL);
        if (!updatedOwnerAffiliation) {
          await auth.signOut();
          throw new Error('Access denied: Owner role required.');
        }
      }

      // If defaultAffiliation is not owner but user has owner role, switch it
      if (defaultAff?.role !== PORTAL || defaultAff?.schoolID !== schoolID) {
        console.log('Default affiliation mismatch, switching to owner...', { current: defaultAff, expected: { role: PORTAL, schoolID } });
        const setDefaultAffiliationFn = httpsCallable(functions, 'setDefaultAffiliation');
        await setDefaultAffiliationFn({ uid: user.uid, role: PORTAL, schoolID });
        // Refresh token to get updated claims
        await user.getIdTokenResult(true);
        console.log('Successfully switched default affiliation to owner');
      }

      localStorage.setItem('portal', PORTAL);
      window.location.href = '/owner-dashboard';
    } catch (e: unknown) {
      throw e as FetchError;
    }
  };

  const handleSocialSignIn = async (provider: GoogleAuthProvider | OAuthProvider, providerName: string): Promise<void> => {
    try {
      // Clear any stale cached auth data BEFORE authentication
      clearStaleAuthData();

      if (!executeRecaptcha) {
        throw new Error('reCAPTCHA not yet loaded. Please try again in a moment.');
      }
      const recaptchaToken = await executeRecaptcha('social_login');

      // Verify recaptcha server-side before performing social sign-in
      const verifyResp = await fetch(`https://us-central1-cbsms-da9ce.cloudfunctions.net/createUser/verify-recaptcha`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recaptchaToken, expectedAction: 'social_login' }),
      });
      if (!verifyResp.ok) {
        const text = await verifyResp.text();
        let data: any = {};
        try { data = JSON.parse(text); } catch {}
        throw new Error(data?.message || `reCAPTCHA verification failed (${verifyResp.status})`);
      }
      const vr = await verifyResp.json();
      if (!vr?.success) throw new Error('reCAPTCHA verification failed');

      const result: UserCredential = await signInWithPopup(auth, provider);
      const user: User = result.user;
      const idTokenResult = await user.getIdTokenResult(true);
      const affiliations = isAffiliationArray(idTokenResult.claims.affiliations)
        ? idTokenResult.claims.affiliations
        : [];
      const defaultAff = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;

      restrictNonOwnerRoles(affiliations, user.email || '');

      const ownerAffiliation = affiliations.find((aff) => aff.role === PORTAL);
      const schoolID = ownerAffiliation?.schoolID ?? 'cbsms-owner';

      if (!ownerAffiliation) {
        await setOwnerClaims(user.uid, user.email || '');
        const refreshedTokenResult = await user.getIdTokenResult(true);
        const updatedAffiliations = isAffiliationArray(refreshedTokenResult.claims.affiliations)
          ? refreshedTokenResult.claims.affiliations
          : [];
        const updatedOwnerAffiliation = updatedAffiliations.find((aff) => aff.role === PORTAL);
        if (!updatedOwnerAffiliation) {
          await auth.signOut();
          throw new Error('Access denied: Owner role required.');
        }
      }

      // If defaultAffiliation is not owner but user has owner role, switch it
      if (defaultAff?.role !== PORTAL || defaultAff?.schoolID !== schoolID) {
        console.log('Default affiliation mismatch, switching to owner...', { current: defaultAff, expected: { role: PORTAL, schoolID } });
        const setDefaultAffiliationFn = httpsCallable(functions, 'setDefaultAffiliation');
        await setDefaultAffiliationFn({ uid: user.uid, role: PORTAL, schoolID });
        // Refresh token to get updated claims
        await user.getIdTokenResult(true);
        console.log('Successfully switched default affiliation to owner');
      }

      localStorage.setItem('portal', PORTAL);
      window.location.href = '/owner-dashboard';
    } catch (e: unknown) {
      const error = e as FetchError;
      setError(error.message || `${providerName} sign-in failed`);
    }
  };

  const verifyAndSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      validateInputs();
      if (!executeRecaptcha) {
        throw new Error('reCAPTCHA not yet loaded. Please try again in a moment.');
      }
      const recaptchaToken = await executeRecaptcha('login');

      // Verify recaptcha server-side before performing owner sign-in
      const verifyResp = await fetch(`https://us-central1-cbsms-da9ce.cloudfunctions.net/createUser/verify-recaptcha`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recaptchaToken, expectedAction: 'login' }),
      });
      if (!verifyResp.ok) {
        const text = await verifyResp.text();
        let data: any = {};
        try { data = JSON.parse(text); } catch {}
        throw new Error(data?.message || `reCAPTCHA verification failed (${verifyResp.status})`);
      }
      const vr = await verifyResp.json();
      if (!vr?.success) throw new Error('reCAPTCHA verification failed');

      await handleSignIn();
    } catch (err: unknown) {
      const error = err as FetchError;
      console.error('Auth process error:', error);
      if (error.code) {
        switch (error.code) {
          case 'auth/invalid-email':
            setError('Invalid email address format.');
            break;
          case 'auth/user-disabled':
            setError('This user account has been disabled.');
            break;
          case 'auth/user-not-found':
            setError('No user found with this email.');
            break;
          case 'auth/wrong-password':
            setError('Incorrect password. Please try again.');
            break;
          default:
            setError(error.message || 'Authentication error');
        }
      } else {
        setError(error.message || 'An unexpected error occurred.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setResetMessage(null);
    if (!forgotPasswordEmail) {
      setError('Please enter your email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotPasswordEmail)) {
      setError('Invalid email format');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, forgotPasswordEmail);
      setResetMessage('Password reset email sent. Please check your inbox.');
      setForgotPasswordEmail('');
      setTimeout(() => setShowForgotPassword(false), 3000);
    } catch (e: unknown) {
      const error = e as FetchError;
      setError(error.message || 'Failed to send password reset email.');
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user: User | null) => {
      if (user && window.location.pathname.includes('/owner-login')) {
        try {
          const idTokenResult: IdTokenResult = await user.getIdTokenResult(true);
          const affiliations = isAffiliationArray(idTokenResult.claims.affiliations)
            ? idTokenResult.claims.affiliations
            : [];
          const defaultAff = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;

          restrictNonOwnerRoles(affiliations, user.email || '');

          const ownerAffiliation = affiliations.find((aff) => aff.role === PORTAL);
          const schoolID = ownerAffiliation?.schoolID ?? 'cbsms-owner';

          if (!ownerAffiliation) {
            await setOwnerClaims(user.uid, user.email || '');
            const refreshedTokenResult = await user.getIdTokenResult(true);
            const updatedAffiliations = isAffiliationArray(refreshedTokenResult.claims.affiliations)
              ? refreshedTokenResult.claims.affiliations
              : [];
            const updatedOwnerAffiliation = updatedAffiliations.find((aff) => aff.role === PORTAL);
            if (!updatedOwnerAffiliation) {
              await auth.signOut();
              setError('Access denied: Owner role required.');
              setIsLoaded(false);
              return;
            }
          }

          // If defaultAffiliation is not owner but user has owner role, switch it
          if (defaultAff?.role !== PORTAL || defaultAff?.schoolID !== schoolID) {
            console.log('Default affiliation mismatch, switching to owner...', { current: defaultAff, expected: { role: PORTAL, schoolID } });
            const setDefaultAffiliationFn = httpsCallable(functions, 'setDefaultAffiliation');
            await setDefaultAffiliationFn({ uid: user.uid, role: PORTAL, schoolID });
            // Refresh token to get updated claims
            await user.getIdTokenResult(true);
            console.log('Successfully switched default affiliation to owner');
          }

          localStorage.setItem('portal', PORTAL);
          window.location.href = '/owner-dashboard';
        } catch (error: unknown) {
          console.error('Error fetching claims:', error);
          setError('Authentication error. Please try again.');
          await auth.signOut();
          setIsLoaded(false);
        }
      } else {
        setIsLoaded(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  return (
    <>
      {isLoaded && <PageLoader isContentReady={false}>{null}</PageLoader>}
      <div className="authe-header">
        <a href="/landing-page">Home</a>
        <a href="#">Features</a>
        <a href="#">Pricing</a>
        <a href="#">About</a>
        <a href="#">Contact Us</a>
      </div>
      <div className="authe-container">
        <form className="authe-form" onSubmit={verifyAndSubmit}>
          <h1 className="authe-heading">Sign In to Sonoma Cloud for {PORTAL_DISPLAY_NAME}</h1>
          <label className="authe-label">
            <input
              type="email"
              name="email"
              placeholder="Email address"
              value={userCredentials.email}
              onChange={handleCredentials}
              className="authe-input"
              required
            />
          </label>
          <label className="authe-label">
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={userCredentials.password}
              onChange={handleCredentials}
              className="authe-input"
              minLength={8}
              required
            />
          </label>
          <h2 className="authe-forgot" onClick={() => setShowForgotPassword(true)} style={{ cursor: 'pointer' }}>
            Forgot Password?
          </h2>
          <button type="submit" className="authe-submit-btn" disabled={isSubmitting}>
            {isSubmitting ? <div className="spinner"></div> : 'Log in'}
          </button>
          {error && <p className="authe-error">{error}</p>}
          <h3 className="authe-agreement">
            By signing in, I agree to the{' '}
            <Link className="authe-link" to="/privacy-policy">
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link className="authe-link" to="/terms-of-service">
              Terms of Service
            </Link>
            .
          </h3>
          <h2 className="authe-or-text">Or sign in with</h2>
          <div className="authe-social-buttons-container">
            <button
              type="button"
              onClick={() => handleSocialSignIn(new GoogleAuthProvider(), 'Google')}
              className="authe-social-btn-google"
            >
              <img src="https://img.icons8.com/?size=100&id=17949&format=png&color=000000" alt="Google" className="social-icon" />
            </button>
            <button
              type="button"
              onClick={() => handleSocialSignIn(new OAuthProvider('apple.com'), 'Apple')}
              className="authe-social-btn-apple"
            >
              <img src="https://img.icons8.com/?size=100&id=30840&format=png&color=000000" alt="Apple" className="social-icon" />
            </button>
          </div>
        </form>

        {showForgotPassword && (
          <div className="authe-popup-overlay">
            <div className="authe-popup">
              <h2>Reset Your Password</h2>
              <form onSubmit={handleForgotPassword}>
                <label className="authe-label">
                  <input
                    type="email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="authe-input"
                    required
                  />
                </label>
                {resetMessage && <p className="authe-success">{resetMessage}</p>}
                {error && <p className="authe-error">{error}</p>}
                <div className="authe-popup-btn-container">
                  <button type="submit" className="authe-popup-btn">Send Reset Link</button>
                  <button
                    type="button"
                    className="authe-popup-btn"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordEmail('');
                      setError(null);
                      setResetMessage(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    
      {/* reCAPTCHA Disclaimer Bottom Right */}
      <div className="fixed bottom-4 right-4 z-50 max-w-[200px] text-right pointer-events-none">
        <p className="text-[8px] font-medium text-gray-400 dark:text-gray-500 leading-tight bg-white/80 dark:bg-black/80 backdrop-blur px-2 py-1 rounded">
          Protected by reCAPTCHA.<br/>
          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline pointer-events-auto hover:text-black">Privacy</a> &{' '}
          <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline pointer-events-auto hover:text-black">Terms</a> apply.
        </p>
      </div>
</>
  );
};

export default OwnerLogin;